import type { Result } from 'true-myth';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    AddTaskInput,
    Alignment,
    AlignmentAutomations,
    AlignmentPosition,
    AlignmentRole,
    AlignmentStep,
    AlignmentTaskDetail,
    AlignmentThread,
    AutomationAdoptionStage,
    AutomationAdoptions,
    AutomationSelectionRef,
    CompareLeft,
    CompareRight,
    CopyBody,
    CopyCompare,
    ManagerDecision,
    MyAlignment,
    PositionAlignments,
    PositionAnswer,
    StartAlignmentInput,
    StartAlignmentsInput,
    StartAlignmentsResult,
    TaskEditInput,
    TaskPractice,
    TeamMember,
    ThreadInput,
} from '../../data-contract/position-alignment';
import {
    alignmentAutomationsSchema,
    alignmentPositionSchema,
    alignmentSchema,
    alignmentTaskDetailSchema,
    alignmentThreadsSchema,
    automationAdoptionsSchema,
    copyCompareSchema,
    myAlignmentSchema,
    positionAlignmentsSchema,
    startAlignmentsResultSchema,
    teamSchema,
} from '../../data-contract/position-alignment';
import type { WorkTaskAutomationRecommendation } from '../../data-contract/task-management';
import { workTaskAutomationRecommendationSchema } from '../../data-contract/task-management';

type RequestContext = { readonly signal?: AbortSignal };

type Fetched<Value> = Promise<Result<Value, HttpFetcherError>>;

type AlignmentTarget = { readonly alignmentId: string };

/** The role a viewer who holds several on the alignment acts and is answered as; a Transformation Lead sends none. */
export type AlignmentActingRole = Exclude<AlignmentRole, 'LEAD'>;

type ActingTarget = AlignmentTarget & { readonly as?: AlignmentActingRole | undefined };

export type AlignmentCommand =
    | { readonly type: 'ANSWER_POSITION'; readonly answer: PositionAnswer; readonly reason?: string }
    | { readonly type: 'SET_TASK_PRACTICE'; readonly taskId: string; readonly practice: TaskPractice }
    | { readonly type: 'ADD_TASK'; readonly task: AddTaskInput }
    | { readonly type: 'EDIT_TASK'; readonly taskId: string; readonly task: TaskEditInput }
    | { readonly type: 'WITHDRAW_TASK'; readonly taskId: string }
    | { readonly type: 'SET_TASK_REMOVED'; readonly taskId: string; readonly removed: boolean }
    | { readonly type: 'MARK_STEP_DONE'; readonly step: AlignmentStep }
    | { readonly type: 'REOPEN_STEP'; readonly step: AlignmentStep }
    | { readonly type: 'VIEW_STEP'; readonly step: AlignmentStep }
    | {
          readonly type: 'DECIDE_STEP';
          readonly step: AlignmentStep;
          readonly decision: Exclude<ManagerDecision, 'PENDING'>;
          readonly reason?: string;
      }
    | { readonly type: 'UNDO_DECISION'; readonly step: AlignmentStep }
    | { readonly type: 'SELECT_AUTOMATIONS'; readonly selections: readonly AutomationSelectionRef[] }
    | { readonly type: 'REJECT_COPY'; readonly reason: string }
    | { readonly type: 'SAVE_COPY'; readonly body: CopyBody }
    | { readonly type: 'ACCEPT' };

type RunAlignmentCommandInput = ActingTarget & {
    readonly command: AlignmentCommand;
    readonly expectedRevision: number;
};

type CommandRequest = {
    readonly method: 'DELETE' | 'POST' | 'PUT';
    readonly path: string;
    readonly payload?: Readonly<Record<string, unknown>>;
};

/** `added_by` and `generation_state` are the server's, read from its work tasks; a saved copy leaves them out. */
const writableCopyBody = (body: CopyBody): CopyBody => ({
    ...body,
    structured: {
        ...body.structured,
        tasks: body.structured.tasks.map(({ added_by: _addedBy, generation_state: _generationState, ...task }) => task),
    },
});

export const alignmentCommandRequest = (command: AlignmentCommand): CommandRequest => {
    switch (command.type) {
        case 'ANSWER_POSITION':
            return {
                method: 'POST',
                path: '/position-answer',
                payload: { answer: command.answer, reason: command.reason ?? null },
            };
        case 'SET_TASK_PRACTICE':
            return {
                method: 'PUT',
                path: `/tasks/${encodeURIComponent(command.taskId)}/practice`,
                payload: command.practice,
            };
        case 'EDIT_TASK':
            return { method: 'PUT', path: `/tasks/${encodeURIComponent(command.taskId)}`, payload: command.task };
        case 'ADD_TASK':
            return { method: 'POST', path: '/tasks', payload: command.task };
        case 'WITHDRAW_TASK':
            return { method: 'DELETE', path: `/tasks/${encodeURIComponent(command.taskId)}` };
        case 'SET_TASK_REMOVED':
            return {
                method: 'PUT',
                path: `/tasks/${encodeURIComponent(command.taskId)}/removed`,
                payload: { removed: command.removed },
            };
        case 'MARK_STEP_DONE':
            return { method: 'POST', path: `/steps/${command.step}/done`, payload: {} };
        case 'REOPEN_STEP':
            return { method: 'POST', path: `/steps/${command.step}/reopen`, payload: {} };
        case 'VIEW_STEP':
            return { method: 'POST', path: `/steps/${command.step}/view`, payload: {} };
        case 'DECIDE_STEP':
            return {
                method: 'POST',
                path: `/steps/${command.step}/decision`,
                payload: { decision: command.decision, reason: command.reason ?? null },
            };
        case 'UNDO_DECISION':
            return { method: 'DELETE', path: `/steps/${command.step}/decision` };
        case 'SELECT_AUTOMATIONS':
            return {
                method: 'PUT',
                path: '/automations/selection',
                payload: {
                    selections: command.selections.map(({ automation_id, task_id }) => ({ automation_id, task_id })),
                },
            };
        case 'REJECT_COPY':
            return { method: 'POST', path: '/copy/reject', payload: { reason: command.reason } };
        case 'SAVE_COPY':
            return {
                method: 'PUT',
                path: '/copy',
                payload: { body: writableCopyBody(command.body) },
            };
        case 'ACCEPT':
            return { method: 'POST', path: '/accept', payload: {} };
    }
};

type AdoptionTarget = AlignmentTarget & AutomationSelectionRef;

type FetchCompareInput = RequestContext &
    AlignmentTarget & { readonly left: CompareLeft; readonly right: CompareRight };

export type PositionAlignmentFetchers = {
    readonly createAlignmentThread: (
        input: AlignmentTarget & { readonly thread: ThreadInput },
    ) => Fetched<AlignmentThread[]>;
    readonly deleteAlignmentThread: (
        input: AlignmentTarget & { readonly threadId: string },
    ) => Fetched<AlignmentThread[]>;
    readonly fetchAlignment: (input: RequestContext & ActingTarget) => Fetched<Alignment>;
    readonly fetchAlignmentAutomations: (input: RequestContext & AlignmentTarget) => Fetched<AlignmentAutomations>;
    readonly fetchAlignmentCompare: (input: FetchCompareInput) => Fetched<CopyCompare>;
    readonly fetchAlignmentPosition: (input: RequestContext & ActingTarget) => Fetched<AlignmentPosition>;
    readonly fetchAlignmentTask: (
        input: RequestContext & ActingTarget & { readonly taskId: string },
    ) => Fetched<AlignmentTaskDetail>;
    readonly fetchAlignmentTaskAutomationRecommendation: (
        input: RequestContext & AlignmentTarget & { readonly taskId: string },
    ) => Fetched<WorkTaskAutomationRecommendation>;
    readonly fetchAlignmentTeam: (input: RequestContext) => Fetched<TeamMember[]>;
    readonly fetchAlignmentThreads: (input: RequestContext & AlignmentTarget) => Fetched<AlignmentThread[]>;
    readonly fetchMyAlignment: (input: RequestContext) => Fetched<MyAlignment>;
    readonly fetchPositionAlignments: (
        input: RequestContext & { readonly positionId: string },
    ) => Fetched<PositionAlignments>;
    readonly removeAutomationAdoption: (input: AdoptionTarget) => Fetched<unknown>;
    readonly resolveAlignmentThread: (
        input: AlignmentTarget & { readonly threadId: string },
    ) => Fetched<AlignmentThread[]>;
    readonly runAlignmentCommand: (input: RunAlignmentCommandInput) => Fetched<Alignment>;
    readonly setAutomationAdoption: (
        input: AdoptionTarget & { readonly stage: AutomationAdoptionStage },
    ) => Fetched<AutomationAdoptions>;
    readonly startAlignment: (input: StartAlignmentInput) => Fetched<Alignment>;
    readonly startAlignments: (input: StartAlignmentsInput) => Fetched<StartAlignmentsResult>;
    readonly startNewAlignment: (input: StartAlignmentInput) => Fetched<Alignment>;
};

type PositionAlignmentFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

const BASE = '/position-alignment';

const alignmentPath = (alignmentId: string, suffix = ''): `/${string}` => `${BASE}/alignments/${alignmentId}${suffix}`;

const EXPECTED_CONFLICTS = [403, 404, 409];

const adoptionPath = ({ alignmentId, automation_id, task_id }: AdoptionTarget): `/${string}` =>
    alignmentPath(
        alignmentId,
        `/automations/${encodeURIComponent(task_id)}/${encodeURIComponent(automation_id)}/adoption`,
    );

export const createPositionAlignmentFetchers = ({
    cobrainerApiFetcher,
    validatingCobrainerApiFetcher: api,
}: PositionAlignmentFetchersDependencies): PositionAlignmentFetchers => ({
    createAlignmentThread: ({ alignmentId, thread }) =>
        api.postJson(alignmentThreadsSchema, {
            expectedStatusCodes: EXPECTED_CONFLICTS,
            pathname: alignmentPath(alignmentId, '/threads'),
            payload: thread,
        }),
    deleteAlignmentThread: ({ alignmentId, threadId }) =>
        api.deleteJson(alignmentThreadsSchema, {
            expectedStatusCodes: EXPECTED_CONFLICTS,
            pathname: alignmentPath(alignmentId, `/threads/${threadId}`),
            payload: undefined,
        }),
    fetchAlignment: ({ alignmentId, as, signal }) =>
        api.get(alignmentSchema, {
            pathname: alignmentPath(alignmentId),
            requestOptions: { searchParams: { as }, signal },
        }),
    fetchAlignmentAutomations: ({ alignmentId, signal }) =>
        api.get(alignmentAutomationsSchema, {
            pathname: alignmentPath(alignmentId, '/automations'),
            requestOptions: { signal },
        }),
    fetchAlignmentCompare: ({ alignmentId, left, right, signal }) =>
        api.get(copyCompareSchema, {
            pathname: alignmentPath(alignmentId, '/copy/compare'),
            requestOptions: { searchParams: { left, right }, signal },
        }),
    fetchAlignmentPosition: ({ alignmentId, as, signal }) =>
        api.get(alignmentPositionSchema, {
            pathname: alignmentPath(alignmentId, '/position'),
            requestOptions: { searchParams: { as }, signal },
        }),
    fetchAlignmentTask: ({ alignmentId, as, signal, taskId }) =>
        api.get(alignmentTaskDetailSchema, {
            pathname: alignmentPath(alignmentId, `/tasks/${encodeURIComponent(taskId)}`),
            requestOptions: { searchParams: { as }, signal },
        }),
    fetchAlignmentTaskAutomationRecommendation: ({ alignmentId, signal, taskId }) =>
        api.get(workTaskAutomationRecommendationSchema, {
            pathname: alignmentPath(alignmentId, `/tasks/${encodeURIComponent(taskId)}/automation-recommendation`),
            requestOptions: { signal },
        }),
    fetchAlignmentTeam: ({ signal }) => api.get(teamSchema, { pathname: `${BASE}/team`, requestOptions: { signal } }),
    fetchAlignmentThreads: ({ alignmentId, signal }) =>
        api.get(alignmentThreadsSchema, {
            pathname: alignmentPath(alignmentId, '/threads'),
            requestOptions: { signal },
        }),
    fetchMyAlignment: ({ signal }) =>
        api.get(myAlignmentSchema, { pathname: `${BASE}/me`, requestOptions: { signal } }),
    fetchPositionAlignments: ({ positionId, signal }) =>
        api.get(positionAlignmentsSchema, {
            pathname: `${BASE}/positions/${positionId}/alignments`,
            requestOptions: { signal },
        }),
    removeAutomationAdoption: (target) =>
        cobrainerApiFetcher.delete({ expectedStatusCodes: EXPECTED_CONFLICTS, pathname: adoptionPath(target) }),
    resolveAlignmentThread: ({ alignmentId, threadId }) =>
        api.postJson(alignmentThreadsSchema, {
            expectedStatusCodes: EXPECTED_CONFLICTS,
            pathname: alignmentPath(alignmentId, `/threads/${threadId}/resolve`),
            payload: {},
        }),
    runAlignmentCommand: ({ alignmentId, as, command, expectedRevision }) => {
        const { method, path, payload } = alignmentCommandRequest(command);
        const pathname = alignmentPath(alignmentId, path);
        if (method === 'DELETE') {
            return api.deleteJson(alignmentSchema, {
                expectedStatusCodes: EXPECTED_CONFLICTS,
                pathname,
                payload: undefined,
                requestOptions: { searchParams: { as, expected_revision: expectedRevision } },
            });
        }
        const request = {
            expectedStatusCodes: EXPECTED_CONFLICTS,
            pathname,
            payload: { ...payload, expected_revision: expectedRevision },
            requestOptions: { searchParams: { as } },
        };

        return method === 'PUT' ? api.putJson(alignmentSchema, request) : api.postJson(alignmentSchema, request);
    },
    setAutomationAdoption: ({ stage, ...target }) =>
        api.putJson(automationAdoptionsSchema, {
            expectedStatusCodes: EXPECTED_CONFLICTS,
            pathname: adoptionPath(target),
            payload: { stage },
        }),
    startAlignment: (input) =>
        api.postJson(alignmentSchema, {
            expectedStatusCodes: EXPECTED_CONFLICTS,
            pathname: `${BASE}/alignments`,
            payload: input,
        }),
    startAlignments: (input) =>
        api.postJson(startAlignmentsResultSchema, { pathname: `${BASE}/alignments/bulk`, payload: input }),
    startNewAlignment: (input) =>
        api.postJson(alignmentSchema, {
            expectedStatusCodes: EXPECTED_CONFLICTS,
            pathname: `${BASE}/alignments/new`,
            payload: input,
        }),
});
